import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useOfferAffiliates } from "@/hooks/use-offers";
import { Plus, Trash2, ShieldAlert } from "lucide-react";

interface CustomCap {
  id: number;
  affiliateId: number;
  affiliateName: string;
  dailyConversionCap: number;
  weeklyConversionCap: number;
  monthlyConversionCap: number;
  globalConversionCap: number;
  dailyClickCap: number;
  weeklyClickCap: number;
  monthlyClickCap: number;
  globalClickCap: number;
}

export function OfferCustomCapsTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "custom-caps"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/custom-caps`);
      if (!res.ok) throw new Error("Failed to fetch custom caps");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (capId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/custom-caps/${capId}`);
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-caps"] }),
  });

  const caps: CustomCap[] = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">{caps.length} custom cap{caps.length !== 1 ? "s" : ""}</h3>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Add Custom Cap
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : caps.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <ShieldAlert className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No per-affiliate caps set. The global offer caps apply.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {caps.map((cap) => (
            <div key={cap.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-sm font-medium text-gray-900">{cap.affiliateName}</span>
                  <span className="text-xs text-gray-400 ml-1">#{cap.affiliateId}</span>
                </div>
                <button onClick={() => { if (window.confirm("Delete this custom cap?")) deleteMutation.mutate(cap.id); }}
                  className="p-1.5 text-red-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {cap.dailyConversionCap > 0 && <CapItem label="Daily Conv." value={cap.dailyConversionCap} />}
                {cap.weeklyConversionCap > 0 && <CapItem label="Weekly Conv." value={cap.weeklyConversionCap} />}
                {cap.monthlyConversionCap > 0 && <CapItem label="Monthly Conv." value={cap.monthlyConversionCap} />}
                {cap.globalConversionCap > 0 && <CapItem label="Global Conv." value={cap.globalConversionCap} />}
                {cap.dailyClickCap > 0 && <CapItem label="Daily Clicks" value={cap.dailyClickCap} />}
                {cap.weeklyClickCap > 0 && <CapItem label="Weekly Clicks" value={cap.weeklyClickCap} />}
                {cap.monthlyClickCap > 0 && <CapItem label="Monthly Clicks" value={cap.monthlyClickCap} />}
                {cap.globalClickCap > 0 && <CapItem label="Global Clicks" value={cap.globalClickCap} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <CustomCapFormModal offerId={offerId} onClose={() => setShowForm(false)} />}
    </div>
  );
}

function CapItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-gray-50 rounded-lg px-3 py-2">
      <span className="text-gray-500">{label}</span>
      <p className="font-semibold text-gray-900 mt-0.5">{value.toLocaleString()}</p>
    </div>
  );
}

function CustomCapFormModal({ offerId, onClose }: { offerId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: affData } = useOfferAffiliates(offerId);
  const assignedAffiliates = affData?.data ?? [];

  const [affiliateId, setAffiliateId] = useState("");
  const [daily, setDaily] = useState("0");
  const [weekly, setWeekly] = useState("0");
  const [monthly, setMonthly] = useState("0");
  const [global_, setGlobal] = useState("0");
  const [dailyClick, setDailyClick] = useState("0");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post(`/v1/offers/${offerId}/custom-caps`, {
        affiliateId: Number(affiliateId),
        dailyConversionCap: Number(daily),
        weeklyConversionCap: Number(weekly),
        monthlyConversionCap: Number(monthly),
        globalConversionCap: Number(global_),
        dailyClickCap: Number(dailyClick),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed"); }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-caps"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">Add Custom Cap</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Affiliate *</label>
            <select required value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900">
              <option value="">Select affiliate...</option>
              {assignedAffiliates.map((a: any) => <option key={a.affiliateId} value={a.affiliateId}>{a.affiliateName} #{a.affiliateId}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Daily Conv. Cap</label>
              <input type="number" min="0" value={daily} onChange={(e) => setDaily(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Weekly Conv. Cap</label>
              <input type="number" min="0" value={weekly} onChange={(e) => setWeekly(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Monthly Conv. Cap</label>
              <input type="number" min="0" value={monthly} onChange={(e) => setMonthly(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Global Conv. Cap</label>
              <input type="number" min="0" value={global_} onChange={(e) => setGlobal(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-500 mb-1">Daily Click Cap</label>
              <input type="number" min="0" value={dailyClick} onChange={(e) => setDailyClick(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
